import React, { useContext, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext/AuthContext";
import "./ChatBubble.css";
import LinkIcon from "../../resources/images/icons/linked.svg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API_BASE_URL } from "../../apiConfig";

interface ChatBubbleProps {
  message: string;
  type: "bot" | "user";
  avatarPath?: string;
  userIconPath?: string;
  sender?: string;
  modelName?: string;
  sources?: {
    name: string;
    url?: string;
    relevance_score: number;
    file_id?: string;
    page?: number;
    source_type?: string;
  }[];
  score?: string;
  isTyping?: boolean;
  collections?: { [key: string]: string }[];
}

function ChatBubble({
  message,
  type,
  avatarPath,
  userIconPath,
  sender,
  modelName,
  sources,
  score,
  isTyping = false,
  collections,
}: ChatBubbleProps) {
  const { token } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const sortedSources =
    sources && sources.length > 0
      ? [...sources].sort(
          (a, b) =>
            (Number(b.relevance_score) || 0) - (Number(a.relevance_score) || 0)
        )
      : [];

  const handlePdfClick = async (fileId: string, page?: number) => {
    if (!token) {
      console.error("No token available for API call.");
      alert("Authentication required. Please log in.");
      return;
    }

    setIsLoading(fileId);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/search/download?file_id=${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
          withCredentials: true,
        }
      );

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pageParam = page ? `#page=${page}` : "";
      window.open(`${pdfUrl}${pageParam}`, "_blank");

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (error) {
      console.error("Error fetching PDF:", error);
      alert("Failed to open the PDF. Please try again.");
    } finally {
      setIsLoading(null);
    }
  };

  const displayModelName =
    type === "bot" && collections && collections.length > 0
      ? modelName
      : "All data collections";

  return (
    <div className={`chat-bubble ${type} ${isTyping ? "typing" : ""}`}>
      <div className="avatar">
        <img src={type === "bot" ? avatarPath : userIconPath} alt="Avatar" />
      </div>
      <div className="message-container">
        {sender && !isTyping && (
          <div className="message-container-upper">
            <div className="sender">
              <span>{sender}</span>
              {type === "bot" && displayModelName && (
                <>
                  <span className="chat-bubble-divider"></span>
                  <span
                    className="chat-bubble-model-name"
                    onClick={() => navigate("/")}
                    style={{ cursor: "pointer" }}
                  >
                    {displayModelName}
                  </span>
                </>
              )}
            </div>
            {type === "bot" && score && (
              <div className="message-container-score">
                <label>
                  Confidence score: {(Number(score) * 10).toFixed(1)}%
                </label>
              </div>
            )}
          </div>
        )}
        <div className="message">
          {isTyping ? (
            <div className="typing-indicator">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
            </div>
          ) : (
            <>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message}
              </ReactMarkdown>
              {sortedSources.length > 0 && (
                <div className="sources">
                  {sortedSources.map((source, index) => {
                    const sourceUrl = source.url;
                    const relevanceScore = Number(source.relevance_score) || 0;
                    const fileId = source.file_id;
                    const page = source.page;
                    const sourceType = source.source_type;

                    return (
                      <span key={index}>
                        <div className="sources-link">
                          <img src={LinkIcon} alt="Link" />
                          {fileId && sourceType === "pdf" ? (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePdfClick(fileId, page);
                              }}
                              style={{ position: "relative" }}
                            >
                              {source.name}
                              {isLoading === fileId && (
                                <span className="loading-spinner">
                                  <span className="spinner"></span>
                                </span>
                              )}
                            </a>
                          ) : sourceUrl ? (
                            <a
                              href={sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {source.name}
                            </a>
                          ) : (
                            <span>{source.name}</span>
                          )}
                          <div>
                            Relevance score: {(relevanceScore * 100).toFixed(1)}
                            %
                          </div>
                        </div>
                      </span>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatBubble;
